import { useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { atom, useAtom, useAtomValue } from 'jotai';
import { cva, type VariantProps } from 'class-variance-authority';
import { isMobile, isSafari } from 'react-device-detect';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ClientOnly } from 'remix-utils';
import { cn } from '@rocco/utils/cn';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselPagination,
    CarouselPrevious,
    CarouselNext,
} from '@rocco/components/carousel';
import { Image } from '@rocco/components/image';
import { ReactComponent as ArrowRight } from '../../../icons/svg/arrow-r.svg';
import type { VideoPlayerError } from '@rocco/components/video-player/video-player';
import { VideoPlayer } from '@rocco/components/video-player/video-player';
import { useLogger } from '@archipro-website/logger/client';
import type { CarouselOptions } from '@rocco/components/carousel/carousel';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { useImpression, useTracker } from '../../../../../tracker/src';

export interface ImageModel {
    src: string;
    alt?: string;
    multiplied?: boolean;
    /** True when this image is the item's current banner. @see ProjectTile */
    isBanner?: boolean;
    /** Underlying LibraryImage ID, needed to set this slide as the banner. */
    libraryImageId?: number;
}

// content-visibility skips layout + paint for off-screen media (the
// expensive part of every tile: image carousels, videos, overlays) while
// find-in-page and the a11y tree keep working. Geometry is safe: every
// variant's height is style-driven (aspect-ratio / h-full), never
// content-driven, so a skipped tile occupies exactly the same box and
// scroll positions / deep-link landings don't shift.
export const _tileMediaVariants = cva('w-full [content-visibility:auto]', {
    variants: {
        aspectRatio: {
            featured: 'h-full', // there's no aspect ratio for featured tile - it should take all available space in the grid
            default: 'aspect-tile-default',
            article: 'aspect-tile-article',
            product: 'aspect-tile-product',
            productTall: 'aspect-tile-product-tall',
            category: 'aspect-tile-category',
            categoryCard: 'aspect-tile-category-card',
            projectBrowse: 'aspect-tile-project-browse',
            professional: 'aspect-tile-professional',
            portrait: 'aspect-tile-portrait',
            square: 'aspect-square',
        },
        rounded: {
            true: 'overflow-hidden rounded-tile',
            false: 'overflow-hidden',
        },
    },
    defaultVariants: {
        aspectRatio: 'default',
        rounded: true,
    },
});

const _tileMediaSizes = cva('', {
    variants: {
        sizes: {
            // yep, it works with any strings not just css classes :)
            featured:
                '(min-width: 1921px) 595px, ((min-width: 1024px) and (max-width: 1920px)) 31vw, 100vw',
            default:
                '(min-width: 1921px) 595px, ((min-width: 1024px) and (max-width: 1920px)) 31vw, 100vw',
            article:
                '(min-width: 1921px) 595px, ((min-width: 1024px) and (max-width: 1920px)) 31vw, 100vw',
            product:
                '(min-width: 1921px) 595px, ((min-width: 1024px) and (max-width: 1920px)) 31vw, 100vw',
            // Same widths as portrait — the two differ in height, not width.
            productTall:
                '(min-width: 1921px) 438px, ((min-width: 1024px) and (max-width: 1920px)) 23vw, 49vw',
            professional:
                '(min-width: 1921px) 595px, ((min-width: 1024px) and (max-width: 1920px)) 24vw, 100vw',
            category:
                '(min-width: 1921px) 595px, ((min-width: 1024px) and (max-width: 1920px)) 31vw, 100vw',
            categoryCard:
                '(min-width: 1921px) 446px, ((min-width: 1024px) and (max-width: 1920px)) 24vw, 70vw',
            projectBrowse:
                '(min-width: 1921px) 592px, ((min-width: 1024px) and (max-width: 1920px)) 31vw, 100vw',
            portrait:
                '(min-width: 1921px) 438px, ((min-width: 1024px) and (max-width: 1920px)) 23vw, 49vw',
            square: '(min-width: 1921px) 348px, ((min-width: 1024px) and (max-width: 1920px)) 18vw, 49vw',
        },
    },
});

const _tileMediaImageVariants = cva('relative h-full overflow-hidden', {
    variants: {
        gradiantOverlay: {
            true: 'after:absolute after:inset-0 after:block after:bg-linear-to-b after:from-transparent after:to-black after:opacity-60',
        },
        multiplied: {
            true: 'bg-[var(--color-clearcut-fill)]',
        },
    },
    defaultVariants: {
        gradiantOverlay: false,
        multiplied: false,
    },
});

/**
 * Atom to track if tile media has been viewed at least once. We want to trigger
 * the impression event only once per user because this event is only used as
 * the activation metric for the A/B test, there is no point triggering more than once.
 */
const tileMediaViewedAtom = atom<boolean>(false);

export type TileMediaAspectRatio = VariantProps<
    typeof _tileMediaVariants
>['aspectRatio'];

interface TileMediaImageProps {
    images: ImageModel[];
    gradiantOverlay?: boolean;
    onSlideChange?: (
        direction: 'CarouselPrev' | 'CarouselNext',
        currentIndex: number,
    ) => void;
}

interface TileMediaVideoProps {
    video: string[];
    fallbackImage?: ImageModel;
}

interface TileMediaProps
    extends
        Partial<TileMediaImageProps>,
        Partial<TileMediaVideoProps>,
        VariantProps<typeof _tileMediaVariants> {
    /**
     * Render an image carousel if there is more than one image. Default is `true`.
     */
    enableTileSlider?: boolean;
    /**
     * When true, images will use eager loading instead of lazy loading.
     * This should be set to true for tiles that are preloaded to ensure
     * the preloaded images are actually used immediately.
     */
    eager?: boolean;
    /**
     * Cap the carousel at five images with a "View more" prompt and disable
     * looping. Project tiles pass false for the infinite-loop carousel.
     */
    limitImageCount?: boolean;
    /**
     * Override the responsive `sizes` attribute normally derived from
     * aspectRatio. Column-aware grids (the browse Display dropdown) pass the
     * real rendered tile width so the srcset picks a sharp-enough file.
     */
    sizes?: string;
}

/**
 * The media component of a Tile. Can display an image, a carousel of images,
 * or a video.
 * @see {@link Tile}
 */
export const TileMedia = (props: TileMediaProps) => {
    const {
        aspectRatio,
        images,
        video,
        gradiantOverlay,
        onSlideChange,
        rounded,
        enableTileSlider = true,
        limitImageCount = true,
        eager,
        sizes: sizesOverride,
    } = props;
    const ref = useRef<HTMLDivElement>(null);
    const [tileMediaViewed, setTileMediaViewed] = useAtom(tileMediaViewedAtom);
    const tracker = useTracker();
    const sizes = sizesOverride ?? _tileMediaSizes({ sizes: aspectRatio });
    const showTileMediaSlider = useMemo(
        () => enableTileSlider && !video?.length && images && images.length > 1,
        [enableTileSlider, video, images],
    );

    // Track the TileMediaImpression event when a tile slider scrolls into view.
    useImpression({
        ref,
        onImpression: async (url: URL) => {
            if (tileMediaViewed || !showTileMediaSlider) return;
            await tracker.log('TileMediaImpression', {
                url,
                targetTracker: ['archiproTracker', 'ga4Tracker'],
            });
            setTileMediaViewed(true);
        },
    });

    return (
        <div
            ref={ref}
            className={cn(
                _tileMediaVariants({ aspectRatio, rounded }),
                'rocco-tile-media',
            )}
        >
            {video?.length ? (
                <TileMediaVideo video={video} fallbackImage={images?.[0]} />
            ) : showTileMediaSlider && images ? (
                <TileMediaImageSlider
                    images={images}
                    sizes={sizes}
                    limitImageCount={limitImageCount}
                    onSlideChange={onSlideChange}
                    eager={eager}
                />
            ) : (
                // Some tiles may not have images, e.g. not live items.
                images?.[0] && (
                    <TileMediaImage
                        image={images[0]}
                        gradiantOverlay={gradiantOverlay}
                        sizes={sizes}
                        eager={eager ?? (!isMobile && isSafari)}
                    />
                )
            )}
        </div>
    );
};

const TileMediaImage = ({
    image,
    gradiantOverlay,
    eager,
    className,
    sizes,
}: {
    image: ImageModel;
    gradiantOverlay?: boolean;
    eager?: boolean;
    className?: string;
    sizes?: string;
}) => {
    return (
        <div
            className={cn(
                _tileMediaImageVariants({
                    gradiantOverlay,
                    multiplied: image.multiplied,
                }),
                className,
            )}
        >
            <Image
                src={image.src}
                alt={image.alt ?? ''}
                fill={true}
                // No mount fade: returning to a listing remounts the whole
                // grid, and fading every already-cached image back in reads
                // as the grid blinking blank. Fresh images pop on decode.
                fadeIn={false}
                loading={eager ? 'eager' : 'lazy'}
                fetchPriority={eager ? 'high' : undefined}
                fit={image.multiplied ? 'contain' : 'cover'}
                multiplied={image.multiplied}
                sizes={sizes}
            />
        </div>
    );
};

/**
 * The maximum number of images to display in the carousel. If the item has
 * more images and the carousel is on the last image, show a "View more" prompt.
 */
const MAX_IMAGES_COUNT = 5;

const TileMediaImageSlider = ({
    images,
    onSlideChange,
    sizes,
    limitImageCount = false,
    eager,
}: TileMediaImageProps & {
    sizes?: string;
    /**
     * Limit the number of images in the carousel and show a "View more" prompt
     * when on the last image and there are more images.
     */
    limitImageCount?: boolean;
    /**
     * When true, the first image will use eager loading instead of lazy loading.
     * This should be set to true for tiles that are preloaded to ensure
     * the preloaded images are actually used immediately.
     */
    eager?: boolean;
}) => {
    const { isDesktop } = useAtomValue(uiContextAtomDisplayMode);
    const location = useLocation();
    const isProductsPage =
        location.pathname === '/products' ||
        location.pathname.startsWith('/products/');
    const [isAtStart, setIsAtStart] = useState(false);
    const [isAtEnd, setIsAtEnd] = useState(false);
    // Render the first two slides so the initial carousel interaction stays smooth.
    const [loadedImageIndexes, setLoadedImageIndexes] = useState<number[]>([
        0, 1,
    ]);
    const stagedIndex = useRef<number>(0);
    const totalImages = images.length; // Add this line to hold total images
    const imagesCount = useMemo(() => {
        return limitImageCount
            ? Math.min(images.length, MAX_IMAGES_COUNT)
            : images.length;
    }, [images.length, limitImageCount]);

    const carouselOptions = useMemo<CarouselOptions>(() => {
        return {
            loop: !limitImageCount,
            watchDrag: (api, evt) => {
                // Only allow dragging when the target is directly in this carousel
                // and not part of a parent carousel drag
                const target = evt.target as Element;
                const carouselElement = api.containerNode();

                return (
                    carouselElement?.contains(target) &&
                    target.closest('.slider-item-image') !== null
                );
            },
        };
    }, [limitImageCount]);

    const handleSlideChange = ({ currentIndex }: { currentIndex: number }) => {
        const isAtStart = currentIndex === 0;
        const isAtEnd = currentIndex === imagesCount - 1;

        setIsAtStart(isAtStart);
        setIsAtEnd(isAtEnd);

        const indexesToLoad = [
            currentIndex,
            isAtStart ? null : currentIndex - 1,
            isAtEnd ? null : currentIndex + 1,
        ].filter((i): i is number => i !== null && i >= 0 && i < imagesCount);

        // Only update if new indexes are needed
        setLoadedImageIndexes((prev) => {
            const toAdd = indexesToLoad.filter((i) => !prev.includes(i));

            return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
        });

        // Optional direction callback
        if (onSlideChange) {
            let direction: 'CarouselNext' | 'CarouselPrev' | undefined;

            if (currentIndex > stagedIndex.current) direction = 'CarouselNext';
            else if (currentIndex < stagedIndex.current)
                direction = 'CarouselPrev';
            else if (
                stagedIndex.current === imagesCount - 1 &&
                currentIndex === 0
            )
                direction = 'CarouselNext';
            else if (
                stagedIndex.current === 0 &&
                currentIndex === imagesCount - 1
            )
                direction = 'CarouselPrev';

            stagedIndex.current = currentIndex;
            if (direction) {
                onSlideChange(direction, currentIndex);
            }
        }
    };

    return (
        <div className="group h-full">
            <Carousel
                options={carouselOptions}
                onChangeSlide={handleSlideChange}
            >
                <CarouselContent>
                    {images.slice(0, imagesCount).map((image, index) => {
                        const isViewMoreImage =
                            limitImageCount &&
                            totalImages > MAX_IMAGES_COUNT &&
                            index === imagesCount - 1;
                        // Only prioritise the first slide when the route explicitly opts in.
                        const shouldEagerLoad = index === 0 && eager === true;
                        // Always render the first slide immediately so eager tiles can consume
                        // the already-requested image without waiting for carousel state.
                        const shouldRender =
                            index === 0 || loadedImageIndexes.includes(index);
                        return (
                            <CarouselItem key={index}>
                                {shouldRender && (
                                    <div className="relative size-full">
                                        <TileMediaImage
                                            className="slider-item-image"
                                            image={image}
                                            sizes={sizes}
                                            eager={shouldEagerLoad}
                                        />
                                        {isViewMoreImage && (
                                            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/80 text-white">
                                                <span
                                                    className={cn(
                                                        'font-medium tracking-[0.8px]',
                                                        isProductsPage
                                                            ? 'text-[1.125rem] leading-[0.375rem] md:text-[1.5rem] md:leading-[0.5rem]'
                                                            : 'text-[1.5rem] leading-[0.5rem]',
                                                    )}
                                                >
                                                    View&nbsp;more
                                                </span>
                                                <span
                                                    className={cn(
                                                        'ml-[0.6rem]',
                                                        isProductsPage
                                                            ? 'w-[1.25rem] md:w-[1.5876rem]'
                                                            : 'w-[1.5876rem]',
                                                    )}
                                                >
                                                    <ArrowRight className="w-full fill-white" />
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CarouselItem>
                        );
                    })}
                </CarouselContent>
                {isDesktop ? (
                    <>
                        <div className="pointer-events-none absolute top-1/2 flex w-full -translate-y-1/2 justify-between px-4 opacity-0 transition-opacity duration-300 ease-linear md:group-hover:opacity-100">
                            <div
                                className={cn(
                                    'flex justify-start',
                                    limitImageCount && isAtStart
                                        ? 'pointer-events-none'
                                        : 'pointer-events-auto',
                                )}
                            >
                                <CarouselPrevious
                                    size="square36"
                                    color="secondary70"
                                    className={cn(
                                        'opacity-100 transition-opacity duration-300',
                                        limitImageCount &&
                                            isAtStart &&
                                            'pointer-events-none opacity-0',
                                    )}
                                >
                                    <ChevronLeft className="mr-0.5" />
                                </CarouselPrevious>
                            </div>
                            <div
                                className={cn(
                                    'flex justify-end',
                                    limitImageCount && isAtEnd
                                        ? 'pointer-events-none'
                                        : 'pointer-events-auto',
                                )}
                            >
                                <CarouselNext
                                    size="square36"
                                    color="secondary70"
                                    className={cn(
                                        'opacity-100 transition-opacity duration-300',
                                        limitImageCount &&
                                            isAtEnd &&
                                            'pointer-events-none opacity-0',
                                    )}
                                >
                                    <ChevronRight className="ml-0.5" />
                                </CarouselNext>
                            </div>
                        </div>
                        <div className="pointer-events-none absolute bottom-4 flex w-full justify-center px-4 opacity-0 transition-opacity duration-300 ease-linear md:group-hover:opacity-100">
                            <div className="pointer-events-auto mt-1.5 flex shrink-0">
                                <CarouselPagination
                                    maxDots={MAX_IMAGES_COUNT}
                                />
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="pointer-events-none absolute bottom-4 flex w-full justify-center px-4">
                        <div className="mt-1.5 flex shrink-0">
                            <CarouselPagination />
                        </div>
                    </div>
                )}
            </Carousel>
        </div>
    );
};

const TileMediaVideo = ({ video, fallbackImage }: TileMediaVideoProps) => {
    const logger = useLogger();

    return (
        <div className="relative h-full overflow-hidden rounded-lg">
            <ClientOnly
                fallback={
                    fallbackImage && <TileMediaImage image={fallbackImage} />
                }
            >
                {() => (
                    <VideoPlayer
                        src={video}
                        fill={true}
                        playsInline={true}
                        controls={false}
                        mode="auto"
                        loop={true}
                        onError={(e?: VideoPlayerError) => {
                            const errorInfo = !e
                                ? 'unknown'
                                : typeof e === 'string'
                                  ? e
                                  : `${e.name}: ${e.message}`;

                            logger.error(
                                `Error while retrieving video. Error: ${errorInfo}`,
                            );
                        }}
                    />
                )}
            </ClientOnly>
        </div>
    );
};
